import React, { useEffect, useState } from "react";
import api, { STAGE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, FolderKanban, CheckCircle2, Clock, AlertTriangle, Users, FileText, Bell, Mail, IndianRupee, DollarSign, ArrowUp, ArrowDown, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import SurveydraftsActivityPanel from "@/components/dashboard/SurveydraftsActivityPanel";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import { Kpi } from "./StatTiles";

export function TeamPerformanceTile({ data }) {
  const monthShort = data.month?.slice(5) ? `${data.month?.slice(5)}/${data.month?.slice(2, 4)}` : data.month;
  const utilColor = (pct) =>
    pct >= 80 ? "bg-green-200" : pct >= 60 ? "bg-yellow-200" : "bg-red-200";

  return (
    <div className="brut-card p-5" data-testid="team-performance-widget">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="label-mono flex items-center gap-1.5">
            <Users className="w-3 h-3" /> MY TEAM&apos;S PERFORMANCE
          </div>
          <div className="font-heading text-xl font-bold">
            {data.headcount} direct report{data.headcount === 1 ? "" : "s"} · {monthShort}
          </div>
          <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
            Utilization = stage-hours logged ÷ attendance hours. Flagged below {data.underperform_threshold ?? 60}%.
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading text-3xl font-black">{data.avg_utilization?.toFixed(0) ?? 0}%</div>
          <div className="label-mono text-zinc-500">AVG UTIL</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="MEDIAN UTIL" value={`${(data.median_utilization || 0).toFixed(0)}%`} testid="team-perf-median" />
        <Kpi label="ATTENDANCE HRS" value={(data.total_attendance_hours || 0).toFixed(0)} testid="team-perf-att" />
        <Kpi label="JOB STAGE HRS" value={(data.total_job_stage_hours || 0).toFixed(0)} testid="team-perf-stage" />
        <Kpi
          label="LATE / EARLY-OUT"
          value={`${data.late_marks || 0} / ${data.early_out_marks || 0}`}
          testid="team-perf-marks"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Flagged */}
        <div className="border border-zinc-900" data-testid="team-perf-underperform">
          <div className="bg-red-200 border-b border-zinc-900 px-3 py-1.5 label-mono flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            UNDER {data.underperform_threshold ?? 60}% · {data.under_threshold?.length || 0}
          </div>
          {(!data.under_threshold || data.under_threshold.length === 0) ? (
            <div className="px-3 py-6 text-center text-zinc-500 font-mono text-xs">
              EVERYONE IS HITTING TARGET — NICE 🎉
            </div>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {data.under_threshold.slice(0, 6).map((r) => (
                <li key={r.user_id} className="px-3 py-2 flex items-center justify-between" data-testid={`under-row-${r.user_id}`}>
                  <span className="font-mono text-sm truncate">{r.user_name}</span>
                  <span className={`font-mono text-xs font-bold border border-zinc-900 px-1.5 py-0.5 ${utilColor(r.utilization_pct)}`}>
                    {r.utilization_pct.toFixed(0)}%
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top performers */}
        <div className="border border-zinc-900" data-testid="team-perf-top">
          <div className="bg-green-200 border-b border-zinc-900 px-3 py-1.5 label-mono flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" />
            TOP PERFORMERS · {data.top_performers?.length || 0}
          </div>
          {(!data.top_performers || data.top_performers.length === 0) ? (
            <div className="px-3 py-6 text-center text-zinc-500 font-mono text-xs">NO DATA YET</div>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {data.top_performers.map((r, i) => (
                <li key={r.user_id} className="px-3 py-2 flex items-center justify-between" data-testid={`top-row-${r.user_id}`}>
                  <span className="flex items-center gap-2 truncate">
                    <span className="font-mono text-[10px] bg-zinc-100 border border-zinc-900 w-5 h-5 flex items-center justify-center font-bold">{i + 1}</span>
                    <span className="font-mono text-sm truncate">{r.user_name}</span>
                  </span>
                  <span className={`font-mono text-xs font-bold border border-zinc-900 px-1.5 py-0.5 ${utilColor(r.utilization_pct)}`}>
                    {r.utilization_pct.toFixed(0)}%
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

